/*
 * Copyright © 2025 Qurasoft GmbH
 */

import {
  type DeserializedRecord,
  deserializeItem,
  deserializeManyRelationship,
  Deserializer,
  deserializeSingleRelationship,
  type JSONAPIManyRelationship,
  type JSONAPIRecord,
  type JSONAPISingleRelationship
} from "../Helpers";
import {deserializeProgram, type Program} from "../Program";
import {type Component, deserializeProgramComponent} from "../program/Component";
import {type ProgramPhase} from "../program/Phase";

const ParticipationStatus = ['not_determined',
  'interested',
  'active',
  'rejected',
  'terminated',
  'not_reachable',
  'dont_call',
  'passive',
  'successful',
  'mostly_successful',
  'partly_successful',
  'not_successful',
  'partly_successful_25',
  'partly_successful_50',
  'partly_successful_75',
  'partly_successful_100',
  'participant_wish',
  'participant_denied'
] as const;

const ParticipantStatus = ['candidate', 'consented', 'former', 'denied'] as const;
type ParticipationStatusType = typeof ParticipationStatus[number];
type ParticipantStatusType = typeof ParticipantStatus[number];

interface PatientProgram extends DeserializedRecord {
  isCurrent: boolean;
  isSelfEnrolled: boolean;
  participationStatus: ParticipationStatusType;
  participantStatus: ParticipantStatusType;
  participationRemark?: string;
  program: Program;
  startedAt: Date;
  programComponents: Component[]
  programPhase?: ProgramPhase
}

interface PatientProgramData extends JSONAPIRecord {
  relationships: {
    program: JSONAPISingleRelationship,
    program_components: JSONAPIManyRelationship,
    program_phase: JSONAPISingleRelationship
  }
}

const deserializePatientProgram: Deserializer<PatientProgram, PatientProgramData> = (data, included) => {
  const program = deserializeSingleRelationship<Program>(data.relationships.program, included, deserializeProgram);
  const programComponents = deserializeManyRelationship<Component>(data.relationships.program_components, included, deserializeProgramComponent);
  const programPhase = deserializeSingleRelationship<ProgramPhase>(data.relationships.program_phase, included);
  const programPhaseId = programPhase?.id;

  return ({
    program,
    programPhase,
    programPhaseId,
    programComponents,
    ...deserializeItem(data)
  });
};

export type {
  PatientProgram, ParticipationStatusType
};

export {
  deserializePatientProgram, ParticipationStatus, ParticipantStatus, ParticipantStatusType
};
